import {
  booleanAttribute,
  Component,
  computed,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  signal,
  Signal,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { GalaxyAbstractFilterInput, GalaxyFilterValueInterface } from '../interface';
import { GalaxyDateDefault, GalaxyFilterOperator } from '../enums';
import { provideGalaxyDateAdapter } from '@vendasta/galaxy/datepicker';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { combineLatest, map, Observable, startWith, Subscription, tap } from 'rxjs';
import { dateDefaultToLabel } from './date-default-labels';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';

export interface DateOption {
  dateOption: GalaxyDateDefault;
  label: string;
}

const yearDateDefaults: GalaxyDateDefault[] = [
  GalaxyDateDefault.DATE_DEFAULT_THIS_YEAR,
  GalaxyDateDefault.DATE_DEFAULT_LAST_YEAR,
  GalaxyDateDefault.DATE_DEFAULT_NEXT_YEAR,
  GalaxyDateDefault.DATE_DEFAULT_INVALID,
];

interface DateFilterInputState {
  operator: GalaxyFilterOperator;
}

@Component({
  selector: 'glxy-date-filter-input',
  templateUrl: './date-filter-input.html',
  styleUrls: ['./date-filter-input.scss'],
  providers: [
    { provide: GalaxyAbstractFilterInput, useExisting: forwardRef(() => GalaxyDateFilterInputComponent) },
    provideGalaxyDateAdapter(),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    TranslateModule,
    MatSelectModule,
    MatButtonModule,
    MatInputModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatDatepickerModule,
  ],
})
export class GalaxyDateFilterInputComponent implements GalaxyAbstractFilterInput, OnDestroy {
  private hasSeenInitialValueInput = false;
  private isApplyingInitialValue = false;

  state: WritableSignal<DateFilterInputState> = signal({
    operator: GalaxyFilterOperator.FILTER_OPERATOR_IS,
  });

  @Input() set operator(value: GalaxyFilterOperator) {
    this.state.update((state) => ({ ...state, operator: value }));
    if (
      value === GalaxyFilterOperator.FILTER_OPERATOR_ANNIVERSARY_IS &&
      this.hasSeenInitialValueInput &&
      !this.isApplyingInitialValue
    ) {
      const selectedDateDefault = this.dateDefaultControl.value;
      if (
        selectedDateDefault === GalaxyDateDefault.DATE_DEFAULT_INVALID ||
        yearDateDefaults.includes(selectedDateDefault)
      ) {
        this.dateDefaultControl.setValue(GalaxyDateDefault.DATE_DEFAULT_TODAY);
      }
      this.dateControl.setValue(undefined);
    }
  }

  @Input() set initialValue(value: GalaxyFilterValueInterface[]) {
    this.hasSeenInitialValueInput = true;
    if (value?.length > 0 && value[0].date) {
      this.isApplyingInitialValue = true;
      this.dateDefaultControl.setValue(GalaxyDateDefault.DATE_DEFAULT_INVALID);
      window.setTimeout(() => {
        if (value?.length > 0 && value[0].date) {
          if (value.length > 1 && value[1].date) {
            this.startDateControl.setValue(value[0].date);
            this.endDateControl.setValue(value[1].date);
          } else {
            const toUTC = new Date(value[0].date);
            const day = toUTC.getUTCDate().toString().padStart(2, '0');
            const month = (toUTC.getUTCMonth() + 1).toString().padStart(2, '0');
            const year = toUTC.getUTCFullYear();
            this.dateControl.setValue(`${year}-${month}-${day}`);
          }
        }
        this.isApplyingInitialValue = false;
      }, 0);
      this.valueChanges.emit(value);
    } else if (value?.length > 0 && value[0].dateDefault) {
      this.isApplyingInitialValue = true;
      window.setTimeout(() => {
        if (value?.length > 0 && value[0].dateDefault) {
          this.dateDefaultControl.setValue(value[0].dateDefault, { emitEvent: true });
        }
        this.isApplyingInitialValue = false;
      }, 0);
      this.valueChanges.emit(value);
    }
  }

  @Input({ transform: booleanAttribute }) set disabled(value: boolean) {
    if (value) {
      this.dateControl.disable();
      this.startDateControl.disable();
      this.endDateControl.disable();
      this.dateDefaultControl.disable();
    }
  }

  @Output() valueChanges = new EventEmitter<GalaxyFilterValueInterface[]>();

  dateDefaultOptions: Signal<DateOption[]> = computed(() => {
    const operator = this.state().operator;
    if (
      operator === GalaxyFilterOperator.FILTER_OPERATOR_IS_BETWEEN ||
      operator === GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_BETWEEN
    ) {
      this.dateDefaultControl.setValue(GalaxyDateDefault.DATE_DEFAULT_INVALID);
      return dateDefaultToLabel.filter((option) => option.dateOption === GalaxyDateDefault.DATE_DEFAULT_INVALID);
    }
    if (operator === GalaxyFilterOperator.FILTER_OPERATOR_ANNIVERSARY_IS) {
      return dateDefaultToLabel.filter((option) => !yearDateDefaults.includes(option.dateOption));
    }
    return dateDefaultToLabel;
  });

  dateDefaultControl: FormControl<GalaxyDateDefault> = new FormControl<GalaxyDateDefault>(
    GalaxyDateDefault.DATE_DEFAULT_INVALID,
    {
      nonNullable: true,
    },
  );

  dateControl: FormControl<string | undefined> = new FormControl<string | undefined>(undefined, { nonNullable: true });
  startDateControl: FormControl<Date | undefined> = new FormControl<Date | undefined>(undefined, { nonNullable: true });
  endDateControl: FormControl<Date | undefined> = new FormControl<Date | undefined>(undefined, { nonNullable: true });

  showDate$: Observable<boolean> = this.dateDefaultControl.valueChanges.pipe(
    startWith(GalaxyDateDefault.DATE_DEFAULT_INVALID),
    map((dateDefault) => {
      return dateDefault === GalaxyDateDefault.DATE_DEFAULT_INVALID;
    }),
  );

  showDateRangePicker: Signal<boolean> = computed(() => {
    const operator = this.state().operator;
    return (
      operator === GalaxyFilterOperator.FILTER_OPERATOR_IS_BETWEEN ||
      operator === GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_BETWEEN
    );
  });

  subscriptions: Subscription[] = [];

  constructor() {
    this.subscriptions.push(
      this.dateDefaultControl.valueChanges
        .pipe(
          tap((value) => {
            if (value !== GalaxyDateDefault.DATE_DEFAULT_INVALID) {
              this.valueChanges.emit([{ dateDefault: value }]);
            }
          }),
        )
        .subscribe(),
    );

    this.subscriptions.push(
      this.dateControl.valueChanges
        .pipe(
          tap((value) => {
            if (value) {
              this.valueChanges.emit([{ date: new Date(value) }]);
            }
          }),
        )
        .subscribe(),
    );

    this.subscriptions.push(
      combineLatest([this.startDateControl.valueChanges, this.endDateControl.valueChanges])
        .pipe(
          tap(([startDate, endDate]) => {
            if (startDate && endDate) {
              this.valueChanges.emit([{ date: startDate }, { date: endDate }]);
            }
          }),
        )
        .subscribe(),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  clearValue() {
    this.dateDefaultControl.setValue(GalaxyDateDefault.DATE_DEFAULT_INVALID);
    this.dateControl.setValue(undefined);
  }
}
